class ShopByBestSellers extends HTMLElement {
  connectedCallback() {
    this._onDocumentClick = this._handleDocumentClick.bind(this);

    document.addEventListener('click', this._onDocumentClick);
    // Prevent document click from closing hotspots when clicking inside this section
    this.addEventListener('click', (e) => e.stopPropagation());

    this._initHotspots();
    this._initQuickView();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('resize', this._onResize);
  }

  // ── Hotspots ─────────────────────────────────────────────────

  _initHotspots() {
    const wrappers = this.querySelectorAll('.ec-hotspot-wrapper');

    wrappers.forEach((wrapper) => {
      this._setHotspotDirection(wrapper);
    });

    wrappers.forEach((wrapper) => {
      const btn = wrapper.querySelector('.ec-hotspot');
      if (!btn) return;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const handle = btn.getAttribute('data-product-handle');
        const url = btn.getAttribute('data-product-url');
        if (handle) this._openQuickView(handle, url);
      });
    });
  }

  _handleDocumentClick() {
    // No-op: desktop uses CSS :hover; mobile opens quickview directly
  }

  _setHotspotDirection(wrapper) {
    const panel = wrapper.closest('.sbs-image-panel');
    if (!panel) return;

    const leftPct = parseFloat(wrapper.style.left) || 50;
    const topPct = parseFloat(wrapper.style.top) || 50;
    const panelW = panel.offsetWidth || 720;
    const panelH = panel.offsetHeight || 888;

    // Card is 263px wide, hotspot button is 40px, gap is 16px
    const hotspotX = (leftPct / 100) * panelW;
    if (hotspotX + 40 + 16 + 263 > panelW) {
      wrapper.classList.add('opens-left');
    }

    // Card approx 160px tall; opens up if card would overflow bottom
    const hotspotY = (topPct / 100) * panelH;
    if (hotspotY + 160 > panelH) {
      wrapper.classList.add('opens-up');
    }
  }

  _quickAdd(btn, variantId) {
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
    })
      .then((res) => {
        if (!res.ok) throw new Error('Cart add failed');
        return res.json();
      })
      .then(() => {
        btn.textContent = 'Added \u2713';
        return fetch('/cart.js').then((r) => r.json());
      })
      .then((cart) => {
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            data: { source: 'product-form-component', itemCount: cart.item_count }
          }
        }));
        setTimeout(() => {
          btn.disabled = false;
          btn.textContent = originalText;
        }, 2000);
      })
      .catch(() => {
        btn.disabled = false;
        btn.textContent = originalText;
      });
  }

  // ── Quick View ────────────────────────────────────────────────

  _initQuickView() {
    this._quickview = this.closest('.sbs-section')?.querySelector('.ec-quickview');
    if (!this._quickview) return;

    this._qvDrawer = this._quickview.querySelector('.ec-quickview__drawer');
    this._qvContent = this._quickview.querySelector('.ec-quickview__content');
    this._qvLoading = this._quickview.querySelector('.ec-quickview__loading');
    this._qvOverlay = this._quickview.querySelector('.ec-quickview__overlay');

    this._qvOverlay?.addEventListener('click', () => this._closeQuickView());

    this._onKeyDown = (e) => {
      if (e.key === 'Escape') this._closeQuickView();
    };
    document.addEventListener('keydown', this._onKeyDown);

    let resizeTimer;
    this._onResize = () => {
      if (this._qvDrawer) {
        this._qvDrawer.style.transition = 'none';
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this._qvDrawer.style.transition = '';
        }, 300);
      }
      this.querySelectorAll('.ec-hotspot-wrapper.is-active').forEach((w) => {
        w.classList.remove('is-active');
      });
    };
    window.addEventListener('resize', this._onResize);
  }

  _openQuickView(handle, productUrl) {
    if (!this._quickview) return;
    this._quickview.setAttribute('aria-hidden', 'false');
    this._quickview.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    this._qvContent.innerHTML = '';
    this._qvLoading?.classList.add('is-active');

    fetch(`/products/${handle}?sections=ec-quickview-product`)
      .then((res) => {
        if (!res.ok) throw new Error('Fetch failed');
        return res.json();
      })
      .then((data) => {
        const html = data['ec-quickview-product'];
        if (!html) throw new Error('Section returned null');
        this._qvContent.innerHTML = html;
        this._qvLoading?.classList.remove('is-active');
        this._initQuickViewInteractions(productUrl);
        this._reloadStampedBadges();
      })
      .catch(() => {
        this._qvLoading?.classList.remove('is-active');
        this._qvContent.innerHTML =
          '<div class="ec-qv__panel" style="justify-content:center;align-items:center">' +
          '<p style="padding:16px;font-family:Figtree,sans-serif;font-size:14px;color:#555">' +
          'Could not load product. <a href="' + productUrl + '" style="color:#1f1f1f;text-decoration:underline">View full details</a></p></div>';
      });
  }

  _initQuickViewInteractions(productUrl) {
    const content = this._qvContent;

    // Progress bar for mobile image carousel
    const qvImages = content.querySelector('.ec-qv__images');
    const qvIndicator = content.querySelector('.ec-qv__progress-indicator');
    if (qvImages && qvIndicator) {
      const updateQvProgress = () => {
        const maxScroll = qvImages.scrollWidth - qvImages.clientWidth;
        if (maxScroll <= 0) {
          qvIndicator.style.width = '100%';
          qvIndicator.style.left = '0%';
          return;
        }
        const track = qvIndicator.parentElement;
        const trackW = track.clientWidth;
        const imageCount = qvImages.querySelectorAll('.ec-qv__image-item').length;
        let indicatorW;
        if (imageCount === 2) {
          indicatorW = 30;
        } else {
          const visibleRatio = qvImages.clientWidth / qvImages.scrollWidth;
          const indicatorPx = Math.min(Math.max(visibleRatio * trackW, trackW * 0.08), 42);
          indicatorW = (indicatorPx / trackW) * 100;
        }

        const progress = maxScroll > 0 ? qvImages.scrollLeft / maxScroll : 0;
        qvIndicator.style.width = indicatorW + '%';
        qvIndicator.style.left = progress * (100 - indicatorW) + '%';
      };
      qvImages.addEventListener('scroll', updateQvProgress, { passive: true });
      updateQvProgress();
    }

    // Horizontal drag for image carousel on mobile
    // CSS touch-action: pan-y lets the browser handle vertical scroll natively,
    // so we handle horizontal drag via JS instead of native overflow scroll.
    if (qvImages && window.innerWidth < 750) {
      let dragStartX = 0;
      let scrollStart = 0;
      let dragging = false;

      qvImages.addEventListener('touchstart', (e) => {
        dragStartX = e.touches[0].clientX;
        scrollStart = qvImages.scrollLeft;
        dragging = true;
      }, { passive: true });

      qvImages.addEventListener('touchmove', (e) => {
        if (!dragging) return;
        qvImages.scrollLeft = scrollStart + (dragStartX - e.touches[0].clientX);
      }, { passive: true });

      qvImages.addEventListener('touchend', () => {
        if (!dragging) return;
        dragging = false;
        // Snap to nearest image
        const item = qvImages.querySelector('.ec-qv__image-item');
        if (item) {
          const w = item.offsetWidth;
          const nearest = Math.round(qvImages.scrollLeft / w) * w;
          qvImages.scrollTo({ left: nearest, behavior: 'smooth' });
        }
      }, { passive: true });
    }

    const jsonEl = content.querySelector('script[data-ec-qv-product]');
    let productData = null;
    if (jsonEl) {
      try { productData = JSON.parse(jsonEl.textContent); } catch (e) { /* noop */ }
    }

    const selectedOptions = [];
    if (productData) {
      const selectedVariant = productData.variants.find((v) => v.id === productData.selected_variant_id);
      if (selectedVariant) {
        selectedVariant.options.forEach((opt) => selectedOptions.push(opt));
      }
    }

    const closeBtn = content.querySelector('.ec-qv__close');
    closeBtn?.addEventListener('click', () => this._closeQuickView());

    content.querySelectorAll('.ec-qv__swatch, .ec-qv__size, .ec-qv__option-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.optionIndex, 10);
        const value = btn.dataset.value;

        const parent = btn.closest('.ec-qv__option');
        parent.querySelectorAll('.ec-qv__swatch, .ec-qv__size, .ec-qv__option-btn').forEach((b) => {
          b.classList.remove('is-selected');
        });
        btn.classList.add('is-selected');

        const selectedLabel = parent.querySelector('[data-qv-option-selected]');
        if (selectedLabel) selectedLabel.textContent = value.toUpperCase();

        selectedOptions[idx] = value;
        if (productData) {
          const variant = productData.variants.find((v) =>
            v.options.every((opt, i) => opt === selectedOptions[i])
          );
          if (variant) this._updateQuickViewVariant(variant);
        }
      });
    });

    const addBtn = content.querySelector('.ec-qv__add-to-bag');
    addBtn?.addEventListener('click', () => {
      const variantId = addBtn.dataset.variantId;
      if (!variantId || addBtn.disabled) return;
      this._qvAddToCart(addBtn, variantId);
    });

    const saveBtn = content.querySelector('.ec-qv__save');
    saveBtn?.addEventListener('click', () => {
      if (productData) {
        this.dispatchEvent(new CustomEvent('wishlist:add', {
          bubbles: true,
          detail: { productId: productData.id, url: productData.url }
        }));
      }
    });
  }

  _updateQuickViewVariant(variant) {
    const content = this._qvContent;

    if (variant.featured_image) this._scrollToVariantImage(variant.featured_image);

    const priceEl = content.querySelector('[data-qv-price]');
    if (priceEl) {
      let priceHtml = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceHtml += '<span class="ec-qv__compare-price">' + variant.compare_at_price_formatted + '</span>';
      }
      priceHtml += '<span class="ec-qv__current-price">' + variant.price_formatted + '</span>';
      priceEl.innerHTML = priceHtml;
    }

    const addBtn = content.querySelector('.ec-qv__add-to-bag');
    if (addBtn) {
      addBtn.dataset.variantId = variant.id;
      if (variant.available) {
        addBtn.disabled = false;
        addBtn.innerHTML = 'ADD TO BAG <span class="ec-qv__bag-plus">[+]</span>';
      } else {
        addBtn.disabled = true;
        addBtn.innerHTML = 'SOLD OUT <span class="ec-qv__bag-plus">[+]</span>';
      }
    }
  }

  _scrollToVariantImage(featuredImageUrl) {
    if (!this._qvContent || !featuredImageUrl) return;
    const firstImg = this._qvContent.querySelector('.ec-qv__image-item img');
    if (!firstImg) return;
    const base = featuredImageUrl.split('?')[0];
    firstImg.src = `${base}?width=870`;
    firstImg.srcset = `${base}?width=435 435w, ${base}?width=870 870w`;
  }

  _qvAddToCart(btn, variantId) {
    const originalHTML = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = 'ADDING... <span class="ec-qv__bag-plus">[+]</span>';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
      .then((res) => {
        if (!res.ok) throw new Error('Cart add failed');
        return res.json();
      })
      .then(() => {
        btn.innerHTML = 'ADDED ✓ <span class="ec-qv__bag-plus">[+]</span>';

        this._closeQuickView();

        // Fetch updated cart to get item_count for header badge
        return fetch('/cart.js').then((r) => r.json());
      })
      .then((cart) => {
        // Open AMP Slide Cart Drawer if available
        if (typeof window.SLIDECART_UPDATE === 'function') {
          window.SLIDECART_UPDATE();
          if (typeof window.SLIDECART_OPEN === 'function') {
            window.SLIDECART_OPEN();
          }
        }

        // Dispatch cart:update so sf-header badge + cart-drawer update
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, data: { itemCount: cart.item_count } }
        }));

        setTimeout(() => {
          btn.disabled = false;
          btn.innerHTML = originalHTML;
        }, 2000);
      })
      .catch(() => {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      });
  }

  _closeQuickView() {
    if (!this._quickview) return;
    this._quickview.classList.remove('is-open');
    this._quickview.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  // Render Stamped.io review badges inside the quickview.
  
  _reloadStampedBadges() {
    const badges = this._qvContent?.querySelectorAll('.stamped-product-reviews-badge');
    if (!badges || !badges.length) return;

    badges.forEach((badge) => {
      const productId = badge.getAttribute('data-id');
      if (!productId) return;

      const sourceSelector = `.stamped-product-reviews-badge[data-id="${CSS.escape(productId)}"]`;
      const candidates = document.querySelectorAll(sourceSelector);
      let source = null;
      for (const el of candidates) {
        if (el === badge) continue;
        if (this._qvContent?.contains(el)) continue;
        if (el.innerHTML.trim().length > 0 && el.querySelector('.stamped-badge, .stamped-fa-star, .stamped-badge-starrating')) {
          source = el;
          break;
        }
      }

      if (source) {
        badge.innerHTML = source.innerHTML;
        return;
      }

      this._stampedApiReload();
    });

    this._stampedApiReload();
  }

  _stampedApiReload() {
    const tryReload = () => {
      const fn = window.StampedFn;
      if (fn && typeof fn.reloadBadges === 'function') { fn.reloadBadges(); return true; }
      if (fn && typeof fn.loadBadges === 'function') { fn.loadBadges(); return true; }
      if (fn && typeof fn.init === 'function') { fn.init(); return true; }

      const s = window.Stamped || window.stamped;
      if (s && typeof s.reloadBadges === 'function') { s.reloadBadges(); return true; }
      if (s && typeof s.loadBadges === 'function') { s.loadBadges(); return true; }
      if (s && typeof s.init === 'function') { s.init(); return true; }

      if (window.stampedJS && typeof window.stampedJS.reload === 'function') {
        window.stampedJS.reload();
        return true;
      }
      return false;
    };

    if (tryReload()) return;

    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      if (tryReload() || attempts >= 20) clearInterval(interval);
    }, 250);
  }
}

if (!customElements.get('shop-by-best-sellers')) {
  customElements.define('shop-by-best-sellers', ShopByBestSellers);
}
